/**
 * ActionIcons — flat, multi-color illustrated icons for the Orders / PACS /
 * Worklist action columns.
 *
 * These replace the thin single-line Ant Design glyphs with little layered
 * illustrations (in the friendly "flat color" style) so the action columns read
 * as colorful, polished controls. Each icon is a self-contained SVG component:
 *
 *   <NotesIcon size={20} />
 *   <HistoryIcon size={20} disabled />   // grayscale + faded "disabled" look
 *
 * Colors are baked into the SVG (that's what makes them illustrative), so they
 * render correctly regardless of the surrounding table component. The shared
 * `ACTION_HOVER` map gives the matching soft hover-pill tint per action; the
 * `.action-icon-button` wrapper reads it via the `--ai-hover` CSS variable.
 */

import React from 'react';

export interface ActionIconProps {
  /** Rendered width/height in px (icons are square). Default 20. */
  size?: number;
  /** Renders the muted, desaturated + faded "disabled" appearance. */
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

// Soft hover-pill tint per action, tuned to each icon's dominant hue. Consumed
// by `.action-icon-button` via the `--ai-hover` custom property.
export const ACTION_HOVER: Record<string, string> = {
  notes: '#EEF2FF',
  info: '#E3EDFD',
  history: '#FBEFD9',
  upload: '#E8EAFB',
  download: '#E8EAFB',
  viewer: '#E3EDFD',
  share: '#EEF2FF',
  report: '#EEF2FF',
  series: '#E6EAFB',
  more: '#E6EAFB',
  delete: '#FBE3E3',
  edit: '#FBEFD9',
  split: '#EEF2FF',
  alert: '#FCE6E6',
};

// Shared wrapper: sizes the SVG and applies the disabled treatment once.
const Svg: React.FC<ActionIconProps & { children: React.ReactNode }> = ({
  size = 20,
  disabled = false,
  className,
  style,
  children,
}) => (
  <svg
    viewBox="0 0 48 48"
    width={size}
    height={size}
    className={className}
    aria-hidden="true"
    style={{
      display: 'block',
      flexShrink: 0,
      ...(disabled ? { filter: 'grayscale(100%)', opacity: 0.5 } : null),
      ...style,
    }}
  >
    {children}
  </svg>
);

export const HistoryIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="13" y="35" width="6" height="10" rx="3" transform="rotate(20 16 40)" fill="#2B3A67" />
    <rect x="29" y="35" width="6" height="10" rx="3" transform="rotate(-20 32 40)" fill="#2B3A67" />
    <rect x="9" y="8" width="13" height="11" rx="5.5" transform="rotate(-32 15 13)" fill="#6C7AE0" />
    <rect x="26" y="8" width="13" height="11" rx="5.5" transform="rotate(32 33 13)" fill="#6C7AE0" />
    <path d="M14 12 Q24 3 34 12" fill="none" stroke="#2B3A67" strokeWidth="2.6" strokeLinecap="round" />
    <rect x="20.5" y="6.5" width="7" height="5" rx="2.5" fill="#2B3A67" />
    <circle cx="24" cy="27" r="17" fill="#F4A93C" />
    <circle cx="24" cy="27" r="12.5" fill="#EEF1FB" />
    <path d="M24 27 L24 18" stroke="#2B3A67" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M24 27 L19 23" stroke="#2B3A67" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="24" cy="27" r="1.7" fill="#2B3A67" />
    <circle cx="24" cy="17.5" r="1" fill="#2B3A67" />
    <circle cx="33.5" cy="27" r="1" fill="#2B3A67" />
    <circle cx="24" cy="36.5" r="1" fill="#2B3A67" />
    <circle cx="14.5" cy="27" r="1" fill="#2B3A67" />
  </Svg>
);

export const NotesIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="10" y="7" width="24" height="32" rx="4" fill="#C7D0F7" />
    <rect x="14" y="11" width="24" height="32" rx="4" fill="#EEF2FF" />
    <rect x="14" y="11" width="24" height="8" rx="4" fill="#6C7AE0" />
    <rect x="18" y="24" width="16" height="2.4" rx="1.2" fill="#AEB8F0" />
    <rect x="18" y="29" width="16" height="2.4" rx="1.2" fill="#AEB8F0" />
    <rect x="18" y="34" width="10" height="2.4" rx="1.2" fill="#AEB8F0" />
  </Svg>
);

export const InfoIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <circle cx="24" cy="24" r="17" fill="#4263EB" />
    <path d="M11 17 A17 17 0 0 1 37 17 Z" fill="#5A78F0" opacity="0.6" />
    <circle cx="24" cy="16.5" r="2.4" fill="#fff" />
    <rect x="21.8" y="21" width="4.4" height="13" rx="2.2" fill="#fff" />
  </Svg>
);

export const UploadIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="9" y="28" width="30" height="13" rx="4" fill="#6C7AE0" />
    <rect x="9" y="28" width="30" height="6" rx="3" fill="#8B95F2" />
    <path d="M24 9 L33 19 L27 19 L27 30 L21 30 L21 19 L15 19 Z" fill="#F4A93C" />
  </Svg>
);

// Down arrow into a tray (Download Study) — mirrors UploadIcon.
export const DownloadIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="9" y="28" width="30" height="13" rx="4" fill="#6C7AE0" />
    <rect x="9" y="28" width="30" height="6" rx="3" fill="#8B95F2" />
    <path d="M21 9 L27 9 L27 18 L33 18 L24 30 L15 18 L21 18 Z" fill="#F4A93C" />
  </Svg>
);

// Flat eye (Open Viewer).
export const ViewerIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <path d="M5 24 Q24 9 43 24 Q24 39 5 24 Z" fill="#6C7AE0" />
    <circle cx="24" cy="24" r="8.5" fill="#EEF2FF" />
    <circle cx="24" cy="24" r="4.8" fill="#4263EB" />
    <circle cx="26" cy="22" r="1.6" fill="#fff" />
  </Svg>
);

export const ShareIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <path d="M16 24 L33 14 M16 24 L33 34" stroke="#2B3A67" strokeWidth="2.4" strokeLinecap="round" />
    <circle cx="15" cy="24" r="6" fill="#6C7AE0" />
    <circle cx="34" cy="13" r="6" fill="#F4A93C" />
    <circle cx="34" cy="35" r="6" fill="#4263EB" />
  </Svg>
);

export const ReportIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="10" y="8" width="23" height="31" rx="4" fill="#EEF2FF" />
    <rect x="10" y="8" width="23" height="31" rx="4" fill="none" stroke="#C7D0F7" strokeWidth="1.5" />
    <rect x="15" y="15" width="13" height="2.4" rx="1.2" fill="#AEB8F0" />
    <rect x="15" y="20" width="13" height="2.4" rx="1.2" fill="#AEB8F0" />
    <path d="M22 38 L37 23 L41 27 L26 42 L21 43 Z" fill="#F4A93C" />
    <path d="M37 23 L41 27 L38.5 29.5 L34.5 25.5 Z" fill="#2B3A67" />
  </Svg>
);

export const SeriesIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="8" y="14" width="22" height="22" rx="4" fill="#8B95F2" />
    <rect x="18" y="9" width="22" height="22" rx="4" fill="#4263EB" />
    <path d="M29 15.5 V24.5 M24.5 20 H33.5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
  </Svg>
);

export const MoreIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="6" y="18" width="36" height="12" rx="6" fill="#E6EAFB" />
    <circle cx="15" cy="24" r="3.2" fill="#4263EB" />
    <circle cx="24" cy="24" r="3.2" fill="#F4A93C" />
    <circle cx="33" cy="24" r="3.2" fill="#6C7AE0" />
  </Svg>
);

export const DeleteIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="17" y="7" width="14" height="5" rx="2.5" fill="#C0392B" />
    <rect x="10" y="11" width="28" height="5" rx="2.5" fill="#E5484D" />
    <path d="M13 16 L15 41 A2 2 0 0 0 17 43 L31 43 A2 2 0 0 0 33 41 L35 16 Z" fill="#F26B6B" />
    <path d="M20 21 L21 38 M24 21 L24 38 M28 21 L27 38" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" opacity="0.85" />
  </Svg>
);

// Diagonal pencil (DICOM Edit / edit study details).
export const EditIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <g transform="rotate(-45 24 24)">
      <path d="M24 6 L29 14 L19 14 Z" fill="#EBCB9E" />
      <path d="M24 6 L26.2 9.5 L21.8 9.5 Z" fill="#2B3A67" />
      <rect x="19" y="14" width="10" height="20" fill="#F4B73E" />
      <rect x="19" y="14" width="3.2" height="20" fill="#FBD279" />
      <rect x="19" y="34" width="10" height="3.6" fill="#BFC6D4" />
      <rect x="19" y="37.6" width="10" height="5" rx="2" fill="#EC7FA0" />
    </g>
  </Svg>
);

// Two panels with a dashed seam (Split Study).
export const SplitIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <rect x="7" y="13" width="15" height="22" rx="3" fill="#6C7AE0" />
    <rect x="26" y="13" width="15" height="22" rx="3" fill="#4263EB" />
    <line x1="24" y1="9" x2="24" y2="39" stroke="#2B3A67" strokeWidth="2.4" strokeLinecap="round" strokeDasharray="3 3.5" />
  </Svg>
);

// Warning triangle (Emergency case indicator).
export const AlertIcon: React.FC<ActionIconProps> = props => (
  <Svg {...props}>
    <path d="M24 8 L41 38 Q42.5 40.5 39.5 40.5 L8.5 40.5 Q5.5 40.5 7 38 Z" fill="#F03E3E" />
    <rect x="22.4" y="19" width="3.2" height="11" rx="1.6" fill="#fff" />
    <circle cx="24" cy="34.5" r="1.9" fill="#fff" />
  </Svg>
);
